import React from 'react'
import { useTheme } from '@mui/material'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'
import { Call, Profile, Sms } from 'iconsax-react'

import Input from './Input'
import TypesMenu from '../../../TypesMenu'
import StatusMenu from '../../../StatusMenu'
import PhoneInput from '../../../../atoms/PhoneInput'

import { useCustomerContext } from '@SonarCX/providers/CustomerProvider'
import { useAreYouSure } from '@SonarCX/providers/AreYouSureProvider'
import { fetchTypes, selectUserType } from '@SonarCX/slices/userType'
import { getReasonWarningText } from '@SonarCX/utilities'
import useStyles from './styles'

const BasicInfoSection = ({
  phone,
  setPhone,
  phoneError,
  phoneValid,
  email,
  setEmail,
  emailError,
  emailValid,
  hideInternal
}) => {
  const dispatch = useAppDispatch()
  const theme = useTheme()

  const { values, setValues } = useCustomerContext()
  const { setAction, setReason, setSubtext, setTitle, setV2 } = useAreYouSure()
  const userTypes = useAppSelector(selectUserType)

  const classes = useStyles()

  const handleChange = prop => val => {
    setValues(old => ({ ...old, [prop]: val }))
  }

  const userTypeChange = React.useCallback(
    val => {
      if (values.type === '') {
        handleChange('type')(val)
        return
      }
      if (values.type === val) return

      const newType = userTypes.types.find(type => type.id === val)
      const oldType = userTypes.types.find(type => type.id === values.type)

      if (newType?.external === oldType?.external) {
        handleChange('type')(val)
        return
      }

      const reasonTxt = getReasonWarningText(newType, oldType)
      setReason(reasonTxt)
      setSubtext('')
      setTitle('ARE YOU SURE?')
      setAction(() => handleChange('type')(val))
      setV2(true)
    },
    //eslint-disable-next-line
    [
      setAction,
      setReason,
      setSubtext,
      setTitle,
      setV2,
      userTypes.types,
      values.type
    ]
  )

  React.useEffect(() => {
    if (!userTypes.loaded && !userTypes.loading) {
      dispatch(fetchTypes())
    }
  }, [userTypes, dispatch])

  return (
    <div className={classes.root}>
      <Input
        error={emailError}
        valid={emailValid}
        input={
          <input
            placeholder='Email'
            value={email}
            onChange={e => setEmail(e.target.value)}
            className={classes.input}
          />
        }
        className={classes.inputSpacing}
      >
        <div className={classes.label}>
          <Sms color='grey' variant='Bold' size='16' className={classes.icon} />
          <span>Email</span>
        </div>
      </Input>

      <Input
        error={phoneError}
        valid={phoneValid}
        input={
          <PhoneInput
            placeholder='Phone'
            value={phone}
            onChange={e => setPhone(e)}
            borderless
            inputClass={classes.input}
            fontSize={theme.size.sm}
            style={{ marginLeft: '0' }}
          />
        }
        className={classes.inputSpacing}
      >
        <div className={classes.label}>
          <Call
            color='grey'
            variant='Bold'
            size='16'
            className={classes.icon}
          />
          <span>Mobile Phone</span>
        </div>
      </Input>

      <Input
        input={
          <TypesMenu
            selected={values.type}
            onSelect={userTypeChange}
            hideInternal={hideInternal}
          />
        }
        className={classes.dropdownInput}
      >
        <div className={classes.label}>
          <Profile
            color='grey'
            variant='Bold'
            size='16'
            className={classes.icon}
          />
          <span>Type</span>
        </div>
      </Input>

      <Input
        input={
          <StatusMenu
            selected={values.status}
            onSelect={handleChange('status')}
          />
        }
        className={classes.dropdownInput}
      >
        <div className={classes.label}>
          <Profile
            color='grey'
            variant='Bold'
            size='16'
            className={classes.icon}
          />
          <span>Status</span>
        </div>
      </Input>
    </div>
  )
}

export default BasicInfoSection
